import Grid from '@mui/material/Grid';
import {
  selectBodyTypeOptions,
  selectColorTypeOptions,
  selectCylinderTypeOptions,
  selectDoorTypeOptions,
  selectDrivetrainTypeOptions,
  selectFuelTypeOptions,
  selectTransmissionTypeOptions,
} from 'common/hooks/useSelectOptions';
import { SelectBuildOption } from 'common/tools/select-option-tools';
import { SelectOption } from 'common/types/select-option';
import { Formik } from 'formik';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { newListingActions, newListingSelectors } from 'redux/new-listing.slice';
import { RootState } from 'redux/root-reducer';
import { WsFormNativeSelect } from 'stories/forms/WsFormNativeSelect';
import { WsFormTextField } from 'stories/forms/WsFormTextField';
import WsFlexbox from 'stories/layout/WsFlexbox';
import * as yup from 'yup';

import { StepProps } from './CreateListing';
import { ListingRow } from './ListingComponents';
import StepperFooter from './StepperFooter';

interface FormValues {
  colorType: string;
  colorTypeOther?: string;
  bodyType: string;
  bodyTypeOther?: string;
  cylinderType: string;
  cylinderTypeOther?: string;
  doorType: string;
  doorTypeOther?: string;
  drivetrainType: string;
  drivetrainTypeOther?: string;
  fuelType: string;
  fuelTypeOther?: string;
  transmissionType: string;
  transmissionTypeOther?: string;
  description: string;
}

interface FormOptions {
  id: keyof FormValues;
  label: string;
  dispatchFn: (payload: string) => void;
  dispatchOtherFn: (payload: string | undefined) => void;
}

const ListingFeatures = (props: StepProps) => {
  const dispatch = useDispatch();

  const [runOnce, runOnceSet] = React.useState(true);
  const [formOptions, formOptionsSet] = React.useState<FormOptions[]>([]);
  const [validationSchema, validationSchemaSet] = React.useState<any>({});
  const bodyType = useSelector((state: RootState) => newListingSelectors.bodyType(state));
  const bodyTypeOther = useSelector((state: RootState) => newListingSelectors.bodyTypeOther(state));
  const colorType = useSelector((state: RootState) => newListingSelectors.colorType(state));
  const colorTypeOther = useSelector((state: RootState) => newListingSelectors.colorTypeOther(state));
  const cylinderType = useSelector((state: RootState) => newListingSelectors.cylinderType(state));
  const cylinderTypeOther = useSelector((state: RootState) => newListingSelectors.cylinderTypeOther(state));
  const doorType = useSelector((state: RootState) => newListingSelectors.doorType(state));
  const doorTypeOther = useSelector((state: RootState) => newListingSelectors.doorTypeOther(state));
  const drivetrainType = useSelector((state: RootState) => newListingSelectors.drivetrainType(state));
  const drivetrainTypeOther = useSelector((state: RootState) => newListingSelectors.drivetrainTypeOther(state));
  const fuelType = useSelector((state: RootState) => newListingSelectors.fuelType(state));
  const fuelTypeOther = useSelector((state: RootState) => newListingSelectors.fuelTypeOther(state));
  const transmissionType = useSelector((state: RootState) => newListingSelectors.transmissionType(state));
  const transmissionTypeOther = useSelector((state: RootState) => newListingSelectors.transmissionTypeOther(state));
  const description = useSelector((state: RootState) => newListingSelectors.description(state));

  const bodyTypeOptions = React.useMemo(() => selectBodyTypeOptions(SelectBuildOption.includeEmpty), []);
  const colorTypeOptions = React.useMemo(() => selectColorTypeOptions(SelectBuildOption.includeEmpty), []);
  const cylinderTypeOptions = React.useMemo(() => selectCylinderTypeOptions(SelectBuildOption.includeEmpty), []);
  const doorTypeOptions = React.useMemo(() => selectDoorTypeOptions(SelectBuildOption.includeEmpty), []);
  const drivetrainTypeOptions = React.useMemo(() => selectDrivetrainTypeOptions(SelectBuildOption.includeEmpty), []);
  const fuelTypeOptions = React.useMemo(() => selectFuelTypeOptions(SelectBuildOption.includeEmpty), []);

  const transmissionTypeOptions = React.useMemo(() => {
    return selectTransmissionTypeOptions(SelectBuildOption.includeEmpty);
  }, []);

  const allOptions: { [key: string]: SelectOption[] } = {
    colorType: colorTypeOptions,
    bodyType: bodyTypeOptions,
    cylinderType: cylinderTypeOptions,
    doorType: doorTypeOptions,
    drivetrainType: drivetrainTypeOptions,
    fuelType: fuelTypeOptions,
    transmissionType: transmissionTypeOptions,
  };

  useEffect(() => {
    if (runOnce) {
      runOnceSet(false);

      const newFormOptions: FormOptions[] = [
        {
          id: 'colorType',
          label: 'Color',
          dispatchFn: newListingActions.colorType,
          dispatchOtherFn: newListingActions.colorTypeOther,
        },
        {
          id: 'bodyType',
          label: 'Body Style',
          dispatchFn: newListingActions.bodyType,
          dispatchOtherFn: newListingActions.bodyTypeOther,
        },
        {
          id: 'cylinderType',
          label: 'Cylinders',
          dispatchFn: newListingActions.cylinderType,
          dispatchOtherFn: newListingActions.cylinderTypeOther,
        },
        {
          id: 'doorType',
          label: 'Doors',
          dispatchFn: newListingActions.doorType,
          dispatchOtherFn: newListingActions.doorTypeOther,
        },
        {
          id: 'drivetrainType',
          label: 'Drivetrain',
          dispatchFn: newListingActions.drivetrainType,
          dispatchOtherFn: newListingActions.drivetrainTypeOther,
        },
        {
          id: 'fuelType',
          label: 'Fuel',
          dispatchFn: newListingActions.fuelType,
          dispatchOtherFn: newListingActions.fuelTypeOther,
        },
        {
          id: 'transmissionType',
          label: 'Transmission',
          dispatchFn: newListingActions.transmissionType,
          dispatchOtherFn: newListingActions.transmissionTypeOther,
        },
      ];
      formOptionsSet(newFormOptions);

      const validationSchema = yup
        .object()
        .shape(
          newFormOptions.reduce((previous, current) => {
            previous[current.id as keyof FormValues] = yup.string().required('Required');
            previous[`${current.id}Other` as keyof FormValues] = yup.string().when(current.id, {
              is: (value: string) => value === 'other',
              then: yup.string().required('Required'),
            });
            return previous;
          }, {} as any),
        )
        .concat(
          yup.object().shape({
            description: yup.string(),
          }),
        );
      validationSchemaSet(validationSchema);
    }
  }, [
    runOnce,
    formOptions,
    colorTypeOptions,
    bodyTypeOptions,
    cylinderTypeOptions,
    doorTypeOptions,
    drivetrainTypeOptions,
    fuelTypeOptions,
    transmissionTypeOptions,
  ]);

  const formAction = async (formValues: FormValues, setSubmitting: (isSubmitting: boolean) => void) => {
    props.handleNext();
  };

  const onValueChange = (
    event: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLInputElement>,
    formikChange: (event: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLInputElement>) => void,
    dispatchFn: (payload: string) => void,
  ) => {
    // console.log('onChange', event);
    dispatch(dispatchFn(event.target.value));
    formikChange(event);
  };

  return (
    <Formik
      validationSchema={validationSchema}
      // validate={validateForm}
      initialValues={{
        colorType,
        colorTypeOther: colorTypeOther ? colorTypeOther : '',
        bodyType,
        bodyTypeOther: bodyTypeOther ? bodyTypeOther : '',
        cylinderType,
        cylinderTypeOther: cylinderTypeOther ? cylinderTypeOther : '',
        doorType,
        doorTypeOther: doorTypeOther ? doorTypeOther : '',
        drivetrainType,
        drivetrainTypeOther: drivetrainTypeOther ? drivetrainTypeOther : '',
        fuelType,
        fuelTypeOther: fuelTypeOther ? fuelTypeOther : '',
        transmissionType,
        transmissionTypeOther: transmissionTypeOther ? transmissionTypeOther : '',
        description,
      }}
      onSubmit={(values, { setSubmitting }) => formAction(values, setSubmitting)}
    >
      {formik => (
        <>
          <Grid container component="form" onSubmit={formik.handleSubmit}>
            {formOptions.map(x => (
              <Grid container key={x.id}>
                <ListingRow label={x.label} gridLR={[3, 9]}>
                  <WsFlexbox gap={2} sx={{ padding: 0 }}>
                    <WsFormNativeSelect
                      width={150}
                      id={x.id}
                      fsx={{
                        position: 'relative',
                        top: '6px',
                      }}
                      data={allOptions[x.id]}
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                        onValueChange(event, formik.handleChange, x.dispatchFn)
                      }
                      formik={formik}
                    />
                    <WsFormTextField
                      id={`${x.id}Other`}
                      sx={{
                        ...(formik.values[x.id as keyof Omit<FormValues, 'description'>] !== 'other' && {
                          display: 'none',
                        }),
                      }}
                      type="text"
                      size="small"
                      label={`${x.label}`}
                      width={120}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onValueChange(event, formik.handleChange, x.dispatchOtherFn)
                      }
                      formik={formik}
                    />
                  </WsFlexbox>
                </ListingRow>
              </Grid>
            ))}
            <Grid container>
              <ListingRow label="Additional Description" gridLR={[3, 9]} gsx={{ minHeight: '112px' }}>
                <WsFormTextField
                  id="description"
                  multiline
                  variant="outlined"
                  width={360}
                  rows={4}
                  maxLength={240}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onValueChange(event, formik.handleChange, newListingActions.description)
                  }
                  formik={formik}
                  isx={{ padding: 1 }}
                />
              </ListingRow>
            </Grid>
            <StepperFooter
              handleCancel={props.handleCancel}
              isFirst={props.isFirst}
              isLast={props.isLast}
              handleBack={props.handleBack}
              handleNext={formik.handleSubmit}
            />
          </Grid>
        </>
      )}
    </Formik>
  );
};

export default ListingFeatures;
